import { useTranslation } from "react-i18next";
import type { SupportedLanguage } from "../../interfaces";
import type { CongressCountdownCopy } from "../ui/CongressCard";
import type { CongressStatus } from "../../domains/congress/congressStatus";
import { formatCongressDay } from "../../domains/congress/congressDateLabel";
import { DesktopCongressCountdown } from "./DesktopCongressCountdown";
import { DesktopCongressLiveBadge } from "./DesktopCongressLiveBadge";

/**
 * The full-width band under a congress space's header that says where the
 * congress stands (Figma "DesktopCongressPhaseBand"): the countdown before it,
 * LIVE NOW during it, the catch-up window after it. Tinted with the congress's
 * cover colour before, red during, neutral after. A space with no dates draws
 * no band.
 */
export function DesktopCongressPhaseBand({
  status,
  shortName,
  dateLabel,
  coverColor,
  contentLang,
  countdownCopy,
  now,
  onAddToCalendar,
  onFindUs,
}: {
  status: CongressStatus;
  shortName: string;
  /** The dates and the place, under the opening day before the congress */
  dateLabel: string;
  coverColor: string;
  contentLang: SupportedLanguage;
  countdownCopy: CongressCountdownCopy;
  /** Today, named before the day count while the congress is on */
  now: Date;
  /** Null once the congress has started: nothing left to put in a calendar */
  onAddToCalendar: (() => void) | null;
  /**
   * Opens the partner's floorplan (the location document) while the
   * congress is on. Null when the space has none.
   */
  onFindUs: (() => void) | null;
}): React.JSX.Element | null {
  const { t } = useTranslation();

  const content = (() => {
    switch (status.kind) {
      case "countdown":
      case "upcoming":
        return {
          background: `color-mix(in srgb, ${coverColor} 12%, white)`,
          lead:
            status.kind === "countdown" ? (
              <div className="flex flex-col gap-1.5">
                <p className="m-0 text-caption font-bold tracking-wider text-ds-slate-500 uppercase">
                  {t("Starts in")}
                </p>
                <DesktopCongressCountdown
                  countdown={status.countdown}
                  copy={countdownCopy}
                />
              </div>
            ) : null,
          title: t("Opens {{day}}", {
            day: formatCongressDay(status.opensOn, contentLang),
          }),
          detail: dateLabel,
        };
      case "live":
        return {
          background: "var(--color-ds-strawberry-50)",
          lead: (
            <div className="flex flex-col items-start gap-1.5">
              <p className="m-0 text-footnote font-semibold text-ds-slate-500">
                {t("{{date}} · Day {{day}} of {{total}}", {
                  date: formatCongressDay(now, contentLang),
                  day: status.day,
                  total: status.dayCount,
                })}
              </p>
              <DesktopCongressLiveBadge label={countdownCopy.liveNow} />
            </div>
          ),
          title: t("{{congressName}} is on", { congressName: shortName }),
          detail: t("Content shared during the congress appears on this page."),
        };
      case "ended":
        return {
          background: "var(--color-ds-slate-50)",
          lead: (
            <p className="m-0 text-footnote text-ds-slate-600">
              {t("Ended {{day}}", {
                day: formatCongressDay(status.endedOn, contentLang),
              })}
            </p>
          ),
          title: t("Catch up on {{congressName}}", { congressName: shortName }),
          detail:
            status.catchUpUntil === null
              ? t("Everything shared during the congress stays on this page.")
              : t(
                  "Everything shared during the congress stays on this page. Featured until {{day}}.",
                  { day: formatCongressDay(status.catchUpUntil, contentLang) }
                ),
        };
      case "evergreen":
        return null;
      default:
        throw new Error(status satisfies never);
    }
  })();
  if (content === null) return null;

  return (
    <div
      className="flex items-center gap-6 rounded-xl px-5 py-4 font-dm-sans"
      style={{ backgroundColor: content.background }}
    >
      {content.lead}
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <p className="m-0 text-footnote font-semibold text-ds-black">
          {content.title}
        </p>
        <p className="m-0 text-footnote text-ds-slate-600">{content.detail}</p>
      </div>
      {status.kind !== "live" || onFindUs === null ? null : (
        <button
          type="button"
          onClick={onFindUs}
          className="shrink-0 cursor-pointer border-none bg-transparent p-0 text-footnote font-semibold text-ds-mint-800"
        >
          {t("Where to find us")}
        </button>
      )}
      {onAddToCalendar === null ? null : (
        <button
          type="button"
          onClick={onAddToCalendar}
          className="shrink-0 cursor-pointer border-none bg-transparent p-0 text-footnote font-semibold text-ds-mint-800"
        >
          {t("Add to calendar")}
        </button>
      )}
    </div>
  );
}
